import type {
  AdditionalViewerContent,
  PreparedFeature,
  PreparedFeedbackItem,
} from '@frontend/partviewer';

import type { ActionWithPayload } from 'store/types';

import type {
  FeatureLoadingType,
  PreparedPartFileGeometricAnalysisData,
} from './types';

export const types = {
  FEATURE_LOADING_ERROR:
    'common/viewer/cadViewer/geometricAnalysis/FEATURE_LOADING_ERROR',
  FEATURE_LOADING_REQUEST:
    'common/viewer/cadViewer/geometricAnalysis/FEATURE_LOADING_REQUEST',
  FEATURE_LOADING_SUCCESS:
    'common/viewer/cadViewer/geometricAnalysis/FEATURE_LOADING_SUCCESS',
  FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_ERROR:
    'common/viewer/cadViewer/geometricAnalysis/FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_ERROR',
  FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_INIT:
    'common/viewer/cadViewer/geometricAnalysis/FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_INIT',
  FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_SUCCESS:
    'common/viewer/cadViewer/geometricAnalysis/FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_SUCCESS',
  GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_END:
    'common/viewer/cadViewer/geometricAnalysis/GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_END',
  GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_START:
    'common/viewer/cadViewer/geometricAnalysis/GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_START',
  GEOMETRIC_ANALYSIS_QUEUE_REMOVE:
    'common/viewer/cadViewer/geometricAnalysis/GEOMETRIC_ANALYSIS_QUEUE_REMOVE',
  PART_FILE_GEOMETRIC_ANALYSIS_RECEIVE:
    'common/viewer/cadViewer/geometricAnalysis/PART_FILE_GEOMETRIC_ANALYSIS_RECEIVE',
  BULK_GEOMETRIC_ANALYSIS_RECEIVE:
    'common/viewer/cadViewer/geometricAnalysis/BULK_GEOMETRIC_ANALYSIS_RECEIVE',
} as const;

export type FeatureLoadingRequestPayload = {
  features: Array<PreparedFeature | PreparedFeedbackItem>;
  partFileUuid: string;
  type: FeatureLoadingType;
};

export type FeatureLoadingSuccessPayload = {
  featureContentsById: Record<number, AdditionalViewerContent>;
  type: FeatureLoadingType;
};

export type FeatureLoadingErrorPayload = {
  featureIds: number[];
  type: FeatureLoadingType;
};

export type PartFileUuidPayload = {
  partFileUuid: string;
};

export type PartFileGeometricAnalysisPayload = {
  geometricAnalysisData: PreparedPartFileGeometricAnalysisData;
  partFileUuid: string;
};

export type BulkGeometricAnalysisReceivePayload = {
  partFileUuids: string[];
};

export type GeometricAnalysisDataAction =
  | ActionWithPayload<
      typeof types.FEATURE_LOADING_REQUEST,
      FeatureLoadingRequestPayload
    >
  | ActionWithPayload<
      typeof types.FEATURE_LOADING_SUCCESS,
      FeatureLoadingSuccessPayload
    >
  | ActionWithPayload<
      typeof types.FEATURE_LOADING_ERROR,
      FeatureLoadingErrorPayload
    >
  | ActionWithPayload<
      typeof types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_ERROR,
      PartFileUuidPayload
    >
  | ActionWithPayload<
      typeof types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_INIT,
      PartFileUuidPayload
    >
  | ActionWithPayload<
      typeof types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_SUCCESS,
      PartFileGeometricAnalysisPayload
    >
  | ActionWithPayload<
      typeof types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_END,
      PartFileUuidPayload
    >
  | ActionWithPayload<
      typeof types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_START,
      PartFileUuidPayload
    >
  | ActionWithPayload<
      typeof types.GEOMETRIC_ANALYSIS_QUEUE_REMOVE,
      PartFileUuidPayload
    >
  | ActionWithPayload<
      typeof types.PART_FILE_GEOMETRIC_ANALYSIS_RECEIVE,
      PartFileGeometricAnalysisPayload
    >
  | ActionWithPayload<
      typeof types.BULK_GEOMETRIC_ANALYSIS_RECEIVE,
      BulkGeometricAnalysisReceivePayload
    >;

/*
Actions to manage the on demand loading of feature and feedback item additional viewer content
as the user clicks to request seeing this information
*/
export const featureLoading = {
  request: ({
    features,
    partFileUuid,
    type,
  }: FeatureLoadingRequestPayload) => ({
    type: types.FEATURE_LOADING_REQUEST,
    payload: { features, partFileUuid, type },
  }),
  success: ({ featureContentsById, type }: FeatureLoadingSuccessPayload) => ({
    type: types.FEATURE_LOADING_SUCCESS,
    payload: { featureContentsById, type },
  }),
  error: ({ featureIds, type }: FeatureLoadingErrorPayload) => ({
    type: types.FEATURE_LOADING_ERROR,
    payload: { featureIds, type },
  }),
};

export const fetchPartFileGeometricAnalysisData = {
  error: ({ partFileUuid }: PartFileUuidPayload) => ({
    type: types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_ERROR,
    payload: { partFileUuid },
  }),
  init: ({ partFileUuid }: PartFileUuidPayload) => ({
    type: types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_INIT,
    payload: { partFileUuid },
  }),
  success: ({
    geometricAnalysisData,
    partFileUuid,
  }: PartFileGeometricAnalysisPayload) => ({
    type: types.FETCH_PART_FILE_GEOMETRIC_ANALYSIS_DATA_SUCCESS,
    payload: {
      geometricAnalysisData,
      partFileUuid,
    },
  }),
};

export const geometricAnalysisQueue = {
  addToEnd: ({ partFileUuid }: PartFileUuidPayload) => ({
    type: types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_END,
    payload: { partFileUuid },
  }),
  addToStart: ({ partFileUuid }: PartFileUuidPayload) => ({
    type: types.GEOMETRIC_ANALYSIS_QUEUE_ADD_TO_START,
    payload: { partFileUuid },
  }),
  remove: ({ partFileUuid }: PartFileUuidPayload) => ({
    type: types.GEOMETRIC_ANALYSIS_QUEUE_REMOVE,
    payload: { partFileUuid },
  }),
};

export const bulkGeometricAnalysis = {
  receive: ({ partFileUuids }: BulkGeometricAnalysisReceivePayload) => ({
    type: types.BULK_GEOMETRIC_ANALYSIS_RECEIVE,
    payload: { partFileUuids },
  }),
};

export const partFileGeometricAnalysis = {
  receive: ({
    geometricAnalysisData,
    partFileUuid,
  }: PartFileGeometricAnalysisPayload) => ({
    type: types.PART_FILE_GEOMETRIC_ANALYSIS_RECEIVE,
    payload: { geometricAnalysisData, partFileUuid },
  }),
};
